import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { useApi } from '@api/useApi';
import { useConfig } from '@contexts/ConfigContext';
import { useBoardFilters } from '@contexts/BoardFiltersContext';
import {
  useBoard,
  useBoardTasks,
  getNextConnectionFilterSelection,
  isConnectionSyncDisabled,
} from '@onetasklist/shared';
import type {
  SidebarConnectionItem,
  SidebarConnectionsViewModel,
  UseSidebarConnectionsViewModelArgs,
} from './SidebarConnections.ViewModel.types';

const NON_BOARD_ROUTES = new Set(['loading', 'dashboard', 'regions']);

/** How many connections the section shows before the rest go behind "show more". */
export const VISIBLE_CONNECTION_LIMIT = 5;

const isMobileViewport = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches;

export const useSidebarConnectionsViewModel = ({
  onMobileClose,
}: UseSidebarConnectionsViewModelArgs): SidebarConnectionsViewModel => {
  const api = useApi();
  const navigate = useNavigate();
  const { config } = useConfig();
  const boardMatch = useMatch('/:boardId/*');
  const rawBoardId = boardMatch?.params.boardId;
  const boardId =
    rawBoardId && !NON_BOARD_ROUTES.has(rawBoardId) ? rawBoardId : undefined;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isSectionOpen, setIsSectionOpen] = useState(true);

  const { data: board } = useBoard(api.boards, boardId ?? '');
  const boardConnections = useMemo(
    () => (board?.connections ?? []).filter((c) => !c.archived),
    [board],
  );

  const { data: boardTasksData } = useBoardTasks(api.tasks, boardId ?? '', {
    enabled: !!boardId,
  });

  const countByConnectionId = useMemo(() => {
    const map = new Map<string, number>();
    for (const task of boardTasksData?.tasks ?? []) {
      if (task.isDone) continue;
      for (const st of task.sourceTasks) {
        map.set(st.connectionId, (map.get(st.connectionId) ?? 0) + 1);
      }
    }
    return map;
  }, [boardTasksData]);

  // Read from the shared BoardFiltersContext so the sidebar and board stay in sync.
  const { selectedConnectionFilterIds, setSelectedConnectionFilterIds } =
    useBoardFilters();

  const allConnectionFilterIds = useMemo(
    () => boardConnections.map((c) => c.id),
    [boardConnections],
  );

  const sortedConnections = useMemo<SidebarConnectionItem[]>(() => {
    return [...boardConnections]
      .sort((a, b) => {
        const countA = countByConnectionId.get(a.id) ?? -1;
        const countB = countByConnectionId.get(b.id) ?? -1;
        if (countB !== countA) return countB - countA;
        return (a.name ?? a.sourceTypeId).localeCompare(b.name ?? b.sourceTypeId);
      })
      .map((c) => {
        // isSelected reflects any active filter — works for both single and multi-select.
        const isSelected =
          selectedConnectionFilterIds !== null &&
          selectedConnectionFilterIds.includes(c.id);

        // Suspended sync and lapsed authorisation both mean no tasks are
        // arriving, so the rail shows them as one state rather than two.
        const isSyncDisabled = isConnectionSyncDisabled(c.disableUntil, c.lastSync);

        return {
          id: c.id,
          displayName: c.name ?? c.sourceTypeId,
          sourceTypeId: c.sourceTypeId,
          colour: c.colour,
          // Zero rather than absent: the rail is a filter pane, and "0" says
          // this filter would empty the board — a missing badge says nothing.
          count: countByConnectionId.get(c.id) ?? 0,
          isSelected,
          isAuthorised: c.isAuthorised,
          hasFailed: isSyncDisabled || !c.isAuthorised,
          failureReason: isSyncDisabled ? c.disabledReason : undefined,
        };
      });
  }, [boardConnections, countByConnectionId, selectedConnectionFilterIds]);

  const onConnectionClick = useCallback(
    (connectionId: string, isMultiSelect = false) => {
      // A failed connection's entry says "click to fix", so a plain click goes where the
      // fix is: the Connections page, whose Reconnect restores it. Ctrl-click still filters.
      const isFailed = sortedConnections.some(
        (connection) => connection.id === connectionId && connection.hasFailed,
      );
      if (isFailed && !isMultiSelect && boardId) {
        navigate(`/${boardId}/connections`);
        onMobileClose();
        return;
      }

      if (isMobileViewport()) {
        return;
      }

      if (isMultiSelect) {
        // Ctrl/Cmd held: toggle this connection in or out of the multi-select set.
        setSelectedConnectionFilterIds((prev) =>
          getNextConnectionFilterSelection(prev, connectionId, allConnectionFilterIds),
        );
      } else {
        // Plain click: single-select toggle. If already solo-selected, clear.
        const isCurrentlySelected =
          selectedConnectionFilterIds !== null &&
          selectedConnectionFilterIds.length === 1 &&
          selectedConnectionFilterIds[0] === connectionId;

        setSelectedConnectionFilterIds(isCurrentlySelected ? null : [connectionId]);
      }

      onMobileClose();
    },
    [
      sortedConnections,
      boardId,
      navigate,
      selectedConnectionFilterIds,
      setSelectedConnectionFilterIds,
      allConnectionFilterIds,
      onMobileClose,
    ],
  );

  // A selected or failed connection always shows, wherever it sorts: otherwise
  // turning a filter off, or reaching a broken connection, would mean expanding
  // the section to find it again.
  const visibleConnections = useMemo(() => {
    if (isExpanded) {
      return sortedConnections;
    }

    return sortedConnections.filter(
      (connection, index) =>
        index < VISIBLE_CONNECTION_LIMIT ||
        connection.isSelected ||
        connection.hasFailed,
    );
  }, [sortedConnections, isExpanded]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  const toggleSection = useCallback(() => {
    setIsSectionOpen((value) => !value);
  }, []);

  const onAddConnectionClick = useCallback(() => {
    if (boardId) {
      navigate(`/${boardId}/connections/new`);
    }
    onMobileClose();
  }, [navigate, boardId, onMobileClose]);

  const onEditClick = useCallback(
    (connectionId: string) => {
      if (boardId) {
        navigate(`/${boardId}/connections/${encodeURIComponent(connectionId)}/edit`);
      }
      onMobileClose();
    },
    [navigate, boardId, onMobileClose],
  );

  return {
    sortedConnections,
    visibleConnections,
    hiddenCount: sortedConnections.length - visibleConnections.length,
    isExpanded,
    isSectionOpen,
    toggleExpanded,
    toggleSection,
    onAddConnectionClick,
    iconBaseUrl: config?.regionBaseUrl ?? '',
    onConnectionClick,
    onEditClick,
  };
};

